/**
 * ChatLayout — Sidebar + main content area with responsive behavior.
 *
 * Layer: C (UX/CLI)
 * Spec: Uppdrag C §5 (responsive breakpoints), §6
 *
 * @decision Desktop >1024px: fixed 280px sidebar, always visible.
 * @decision Tablet 768–1024px: collapsible 280px sidebar, closes on selection.
 * @decision Mobile <768px: full-width drawer (100% - 60px), overlay with backdrop.
 * @decision Swipe-to-close on mobile via touch events (swipe left).
 * @decision Backdrop: opacity 0.5, click to close.
 * @decision No external dependency — pure CSS + touch handlers.
 */

import {
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';

// ─── Types ────────────────────────────────────────────────────────────

export interface ChatLayoutProps {
  sidebar: ReactNode;
  children: ReactNode;
  /** Callback to notify sidebar about mobile close (passed to sidebar's onCloseMobile) */
  onMobileToggle?: (isOpen: boolean) => void;
}

type Breakpoint = 'mobile' | 'tablet' | 'desktop';

// ─── Breakpoint detection ─────────────────────────────────────────────

function getBreakpoint(): Breakpoint {
  if (typeof window === 'undefined') return 'desktop';
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

// ─── Component ────────────────────────────────────────────────────────

export function ChatLayout({ sidebar, children, onMobileToggle }: ChatLayoutProps) {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>(getBreakpoint);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Touch handling for swipe-to-close
  const touchStartX = useRef<number>(0);
  const touchCurrentX = useRef<number>(0);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // ── Responsive listener ──────────────────────────────────────────────

  useEffect(() => {
    const handleResize = () => {
      const newBp = getBreakpoint();
      setBreakpoint(newBp);
      // Auto-close sidebar when resizing to desktop
      if (newBp === 'desktop') {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Toggle sidebar ───────────────────────────────────────────────────

  const openSidebar = useCallback(() => {
    setIsSidebarOpen(true);
    onMobileToggle?.(true);
  }, [onMobileToggle]);

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
    onMobileToggle?.(false);
  }, [onMobileToggle]);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => {
      const next = !prev;
      onMobileToggle?.(next);
      return next;
    });
  }, [onMobileToggle]);

  // ── Touch handlers (swipe left to close) ─────────────────────────────

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchCurrentX.current = e.touches[0].clientX;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchCurrentX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    const diff = touchStartX.current - touchCurrentX.current;
    // Swipe left threshold: 80px
    if (diff > 80) {
      closeSidebar();
    }
  }, [closeSidebar]);

  // ── Desktop layout: always visible sidebar ───────────────────────────

  if (breakpoint === 'desktop') {
    return (
      <div className="flex h-full bg-bg-primary">
        {/* Sidebar — fixed 280px */}
        <div className="w-[280px] flex-shrink-0 border-r border-border/40">
          {sidebar}
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {children}
        </div>
      </div>
    );
  }

  // ── Tablet/Mobile: collapsible sidebar ───────────────────────────────

  const isDrawer = breakpoint === 'mobile';
  const sidebarWidth = isDrawer ? 'calc(100% - 60px)' : '280px';

  return (
    <div className="relative h-full bg-bg-primary overflow-hidden">
      {/* Hamburger button — only when sidebar is closed */}
      {!isSidebarOpen && (
        <button
          type="button"
          onClick={openSidebar}
          className={[
            'fixed top-3 left-3 z-40',
            'p-2 rounded-lg',
            'bg-bg-secondary text-text-secondary hover:bg-bg-tertiary',
            'backdrop-blur-sm border border-border/40',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
            'transition-colors duration-150',
          ].join(' ')}
          aria-label="Öppna meny"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="4" y1="12" x2="20" y2="12" />
            <line x1="4" y1="6" x2="20" y2="6" />
            <line x1="4" y1="18" x2="20" y2="18" />
          </svg>
        </button>
      )}

      {/* Backdrop */}
      {isSidebarOpen && (
        <div
          className={[
            'fixed inset-0 z-40 bg-black/50',
            'transition-opacity duration-200',
            isSidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
          ].join(' ')}
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar drawer */}
      <div
        ref={sidebarRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        className={[
          'fixed top-0 left-0 z-50 h-full',
          'border-r border-border/40',
          'transition-transform duration-250 ease-out',
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
        style={{ width: sidebarWidth }}
        role="dialog"
        aria-modal="true"
        aria-label="Konversationshistorik"
      >
        {/* Close button inside drawer */}
        <div className="absolute top-3 right-3 z-10">
          <button
            type="button"
            onClick={closeSidebar}
            className={[
              'p-1.5 rounded-lg',
              'text-text-secondary hover:text-text-primary hover:bg-bg-tertiary',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
              'transition-colors duration-150',
            ].join(' ')}
            aria-label="Stäng meny"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        {sidebar}
      </div>

      {/* Main content */}
      <div className={[
        'h-full',
        // Offset for hamburger button
        !isSidebarOpen ? 'pl-0' : '',
      ].join(' ')}>
        {children}
      </div>
    </div>
  );
}
